import { get } from "firebase/database";
import { ref } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FirebaseConfig } from "../config/FirebaseConfig";

class BookingAPI {
  static getBaseUrl() {
    return `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net`;
  }

  static getDatabaseRef() {
    // Import Firebase database instance
    const { getDatabase } = require("firebase/database");
    const { initializeApp } = require("firebase/app");

    const app = initializeApp(FirebaseConfig);
    const database = getDatabase(app);
    return database;
  }

  static getFunctions() {
    const { initializeApp } = require("firebase/app");
    const app = initializeApp(FirebaseConfig);
    return getFunctions(app);
  }

  /**
   * Get single booking by ID
   * @param {string} bookingId - The booking ID
   * @returns {Promise<Object>} - Booking data
   */
  static async getSingleBooking(bookingId) {
    try {
      if (!bookingId) {
        throw new Error('Booking ID is required');
      }

      const database = this.getDatabaseRef();
      const bookingRef = ref(database, `bookings/${bookingId}`);
      const snapshot = await get(bookingRef);

      if (snapshot.exists()) {
        const bookingData = snapshot.val();

        // Add ID to the data
        bookingData.id = bookingId;

        // Add computed fields
        bookingData.pickupAddress = bookingData.pickup?.add ?? '';
        bookingData.dropAddress = bookingData.drop?.add ?? '';
        bookingData.discount = bookingData.discount || 0;
        bookingData.cashPaymentAmount = bookingData.cashPaymentAmount || 0;
        bookingData.cardPaymentAmount = bookingData.cardPaymentAmount || 0;

        return {
          success: true,
          data: bookingData
        };
      } else {
        return {
          success: false,
          error: 'Booking not found'
        };
      }
    } catch (error) {
      console.error('Error fetching single booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get booking tracking data (driver location)
   * @param {string} bookingId - The booking ID
   * @returns {Promise<Object>} - Tracking data
   */
  static async getBookingTracking(bookingId) {
    try {
      if (!bookingId) {
        throw new Error('Booking ID is required');
      }

      const database = this.getDatabaseRef();
      const trackingRef = ref(database, `tracking/${bookingId}`);
      const snapshot = await get(trackingRef);

      if (snapshot.exists()) {
        const trackingData = snapshot.val();
        const locations = Object.keys(trackingData).map(key => trackingData[key]);

        return {
          success: true,
          data: locations
        };
      } else {
        return {
          success: false,
          error: 'No tracking data found'
        };
      }
    } catch (error) {
      console.error('Error fetching booking tracking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get driver information by driver ID
   * @param {string} driverId - The driver ID
   * @returns {Promise<Object>} - Driver data
   */
  static async getDriverInfo(driverId) {
    try {
      if (!driverId) {
        throw new Error('Driver ID is required');
      }

      const database = this.getDatabaseRef();
      const driverRef = ref(database, `users/${driverId}`);
      const snapshot = await get(driverRef);

      if (snapshot.exists()) {
        const driverData = snapshot.val();

        return {
          success: true,
          data: {
            id: driverId,
            name: driverData.name || 'Unknown Driver',
            phone: driverData.phone || '',
            rating: driverData.rating || 0,
            profile_pic: driverData.profile_pic || '',
            usertype: driverData.usertype || 'driver'
          }
        };
      } else {
        return {
          success: false,
          error: 'Driver not found'
        };
      }
    } catch (error) {
      console.error('Error fetching driver info:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get single booking by ID (Public - No Auth Required)
   * @param {string} bookingId - The booking ID
   * @returns {Promise<Object>} - Booking data
   */
  static async getPublicSingleBooking(bookingId) {
    try {
      if (!bookingId) {
        throw new Error('Booking ID is required');
      }

      const functions = this.getFunctions();
      const getPublicBooking = httpsCallable(functions, 'getPublicBooking');

      const result = await getPublicBooking({ bookingId });

      if (result.data.success) {
        return {
          success: true,
          data: result.data.data
        };
      } else {
        return {
          success: false,
          error: result.data.error
        };
      }
    } catch (error) {
      console.error('Error fetching public booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get driver location (Public - No Auth Required)
   * @param {string} driverId - The driver ID
   * @returns {Promise<Object>} - Driver location data
   */
  static async getPublicDriverLocation(driverId) {
    try {
      if (!driverId) {
        throw new Error('Driver ID is required');
      }

      const functions = this.getFunctions();
      const getPublicDriverLocation = httpsCallable(functions, 'getPublicDriverLocation');

      const result = await getPublicDriverLocation({ driverId });

      if (result.data.success) {
        return {
          success: true,
          data: result.data.data
        };
      } else {
        return {
          success: false,
          error: result.data.error
        };
      }
    } catch (error) {
      console.error('Error fetching public driver location:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get driver info (Public - No Auth Required)
   * @param {string} driverId - The driver ID
   * @returns {Promise<Object>} - Driver info data
   */
  static async getPublicDriverInfo(driverId) {
    try {
      if (!driverId) {
        throw new Error('Driver ID is required');
      }

      const functions = this.getFunctions();
      const getPublicDriverInfo = httpsCallable(functions, 'getPublicDriverInfo');

      const result = await getPublicDriverInfo({ driverId });

      if (result.data.success) {
        return {
          success: true,
          data: result.data.data
        };
      } else {
        return {
          success: false,
          error: result.data.error
        };
      }
    } catch (error) {
      console.error('Error fetching public driver info:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }


  /**
   * Get public booking tracking (driver location history) for ShareTrip page
   * @param {string} bookingId - The booking ID
   * @returns {Promise<Object>} - Tracking data (array of locations)
   */
  static async getPublicBookingTracking(bookingId) {
    try {
      if (!bookingId) {
        throw new Error('Booking ID is required');
      }

      const functions = this.getFunctions();
      const getPublicBookingTracking = httpsCallable(functions, 'getPublicBookingTracking');
      const result = await getPublicBookingTracking({ bookingId });

      if (result.data.success) {
        return {
          success: true,
          data: result.data.data
        };
      } else {
        return {
          success: false,
          error: result.data.error
        };
      }
    } catch (error) {
      console.error('Error fetching public booking tracking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default BookingAPI;
